import { useEffect, useMemo, useState } from 'react';
import { toLocalISOString } from '../../../utils/dateUtils';
import type { BasketItem } from '../page';
import ConfirmModal from '../../../components/ConfirmModal';

const PARKED_SALES_STORAGE_KEY = 'posParkedSales';

interface SelectedCustomer {
  id: number;
  name: string;
  phoneNumber?: string | null;
  emailAddress?: string | null;
  predefinedValue?: string | null;
}

interface ParkedSale {
  id: string;
  parkedAt: string;
  parkedBy: string;
  customer: SelectedCustomer | null;
  customerName?: string;
  reference?: string;
  notes?: string;
  itemCount: number;
  total: number;
  items: BasketItem[];
}

interface ParkSaleModalProps {
  onClose: () => void;
  basketItems: BasketItem[];
  selectedCustomer?: SelectedCustomer | null;
  onParkSuccess: () => void;
  onRetrieveSale: (items: BasketItem[], customer?: SelectedCustomer | null) => void;
  initialTab?: 'park' | 'retrieve';
  switchToRetrieveAfterPark?: boolean;
}

const parseJson = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const readParkedSales = (): ParkedSale[] => {
  const data = parseJson<ParkedSale[]>(localStorage.getItem(PARKED_SALES_STORAGE_KEY), []);
  return Array.isArray(data) ? data : [];
};

const writeParkedSales = (sales: ParkedSale[]) => {
  localStorage.setItem(PARKED_SALES_STORAGE_KEY, JSON.stringify(sales));
};

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getNextParkId = (sales: ParkedSale[]) => {
  const highest = sales.reduce((max, sale) => {
    const match = /^PARK-(\d+)$/i.exec(sale.id);
    const current = match ? Number(match[1]) : 0;
    return Math.max(max, current);
  }, 0);
  return `PARK-${String(highest + 1).padStart(3, '0')}`;
};

const getCashierName = () => {
  const counterSession = parseJson<any>(localStorage.getItem('counterSession'), {});
  const userInfo = parseJson<any>(localStorage.getItem('userInfo'), {});
  return counterSession?.user?.name || userInfo?.name || 'Cashier';
};

const normalizeItems = (items: BasketItem[]): BasketItem[] =>
  items.map((item, index) => ({
    ...item,
    line: index + 1,
    qty: toNumber(item.qty, 0),
    price: toNumber(item.price, 0),
    total: toNumber(item.total, toNumber(item.qty, 0) * toNumber(item.price, 0)),
  }));

export default function ParkSaleModal({
  onClose,
  basketItems,
  selectedCustomer = null,
  onParkSuccess,
  onRetrieveSale,
  initialTab = 'park',
  switchToRetrieveAfterPark = false,
}: ParkSaleModalProps) {
  const [activeTab, setActiveTab] = useState<'park' | 'retrieve'>(initialTab);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isParking, setIsParking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [parkedSaleId, setParkedSaleId] = useState('');
  const [parkedSales, setParkedSales] = useState<ParkedSale[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setParkedSales(readParkedSales());
  }, []);

  const currentItems = useMemo(() => normalizeItems(basketItems), [basketItems]);
  const currentItemCount = useMemo(
    () => currentItems.reduce((sum, item) => sum + Math.max(0, toNumber(item.qty, 0)), 0),
    [currentItems]
  );
  const currentTotal = useMemo(
    () => currentItems.reduce((sum, item) => sum + toNumber(item.total, 0), 0),
    [currentItems]
  );

  const handleParkSale = () => {
    if (!currentItems.length || isParking) return;
    setIsParking(true);
    setError(null);

    try {
      const existing = readParkedSales();
      const id = getNextParkId(existing);
      const customerName = reference.trim() || selectedCustomer?.name || undefined;
      const newSale: ParkedSale = {
        id,
        parkedAt: toLocalISOString(),
        parkedBy: getCashierName(),
        customer: selectedCustomer,
        customerName,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        itemCount: currentItems.reduce((sum, item) => sum + Math.max(0, item.qty), 0),
        total: currentItems.reduce((sum, item) => sum + item.total, 0),
        items: currentItems,
      };
      const updated = [newSale, ...existing];
      writeParkedSales(updated);
      setParkedSales(updated);
      setParkedSaleId(id);
      setShowSuccess(true);
      setTimeout(() => {
        onParkSuccess();
        if (switchToRetrieveAfterPark) {
          setShowSuccess(false);
          setReference('');
          setNotes('');
          setActiveTab('retrieve');
        } else {
          onClose();
        }
      }, 1200);
    } catch {
      setError('Failed to park this sale. Please try again.');
    } finally {
      setIsParking(false);
    }
  };

  const handleRetrieveSale = (sale: ParkedSale) => {
    const updated = parkedSales.filter((entry) => entry.id !== sale.id);
    writeParkedSales(updated);
    setParkedSales(updated);
    onRetrieveSale(normalizeItems(sale.items), sale.customer ?? null);
    onClose();
  };

  const handleDeleteSale = (saleId: string) => {
    setDeletingId(saleId);
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-indigo-500 text-white rounded-full mx-auto mb-4">
            <i className="ri-save-line text-5xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 whitespace-nowrap">Sale Parked Successfully!</h2>
          <p className="text-gray-600 mb-4 whitespace-nowrap">Transaction has been saved locally</p>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1 whitespace-nowrap">Reference Number</p>
            <p className="text-3xl font-bold text-indigo-600 whitespace-nowrap">{parkedSaleId}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Park & Retrieve Sales</h2>
            <p className="text-sm text-indigo-100 whitespace-nowrap">
              Save current transaction or retrieve a parked sale
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isParking}
            className="w-8 h-8 flex items-center justify-center hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('park')}
              className={`flex-1 px-6 py-3 font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'park'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-save-line mr-2"></i>
              Park Current Sale
            </button>
            <button
              onClick={() => setActiveTab('retrieve')}
              className={`flex-1 px-6 py-3 font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                activeTab === 'retrieve'
                  ? 'bg-white text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-inbox-unarchive-line mr-2"></i>
              Retrieve Parked Sale ({parkedSales.length})
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'park' ? (
            <div className="space-y-6">
              <div className="p-6 bg-indigo-50 border-2 border-indigo-200 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-indigo-700 mb-1 whitespace-nowrap">Current Transaction</p>
                    <p className="text-3xl font-bold text-indigo-900 whitespace-nowrap">
                      {currentItemCount} Items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-indigo-700 mb-1 whitespace-nowrap">Total Amount</p>
                    <p className="text-3xl font-bold text-indigo-900 whitespace-nowrap">{currentTotal.toFixed(2)}</p>
                  </div>
                </div>

                {currentItems.length > 0 ? (
                  <div className="bg-white rounded-lg p-4 max-h-60 overflow-y-auto">
                    <p className="text-xs font-semibold text-gray-700 mb-2 whitespace-nowrap">Items in Basket:</p>
                    {currentItems.map((item) => (
                      <div
                        key={`${item.line}-${item.code}-${item.name}`}
                        className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-gray-700">
                          {item.qty}x {item.name}
                        </span>
                        <span className="font-semibold text-gray-900 whitespace-nowrap">{item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 text-center">
                    <i className="ri-shopping-basket-line text-4xl text-gray-300 mb-2"></i>
                    <p className="text-sm text-gray-500 whitespace-nowrap">No items in basket</p>
                  </div>
                )}
              </div>

              {currentItems.length > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                      Reference / Customer Name <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={reference}
                      onChange={(e) => setReference(e.target.value)}
                      placeholder="e.g., John Smith, Table 5, Order #123"
                      disabled={isParking}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                      Notes <span className="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes about this transaction..."
                      rows={3}
                      disabled={isParking}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
                      {error}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {parkedSales.length > 0 ? (
                parkedSales.map((sale) => (
                  <div
                    key={sale.id}
                    className="p-5 border-2 border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-4xl font-bold text-gray-900 whitespace-nowrap">{sale.id}</p>
                        <p className="text-xl text-gray-600 whitespace-nowrap">
                          <i className="ri-time-line mr-1"></i>
                          {formatDateTime(sale.parkedAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDeleteSale(sale.id)}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          Delete
                        </button>
                        <button
                          onClick={() => handleRetrieveSale(sale)}
                          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                        >
                          <i className="ri-inbox-unarchive-line mr-1"></i>
                          Retrieve
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Parked By</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.parkedBy}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Items</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.itemCount} items</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 mb-1 whitespace-nowrap">Total</p>
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.total.toFixed(2)}</p>
                      </div>
                    </div>

                    {(sale.customerName || sale.customer?.name) && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 px-3 py-2 rounded">
                        <i className="ri-user-line"></i>
                        <span className="whitespace-nowrap">
                          Customer: {sale.customerName || sale.customer?.name}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-lg font-semibold text-gray-900 mb-2 whitespace-nowrap">No Parked Sales</p>
                  <p className="text-sm text-gray-600 whitespace-nowrap">
                    There are no parked transactions at the moment
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={isParking}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          {activeTab === 'park' && (
            <button
              onClick={handleParkSale}
              disabled={isParking || currentItems.length === 0}
              className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
                isParking || currentItems.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white cursor-pointer'
              }`}
            >
              {isParking ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  Parking Sale...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-2"></i>
                  Park This Sale
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <ConfirmModal
        open={deletingId !== null}
        message="Delete this parked sale?"
        confirmLabel="Delete"
        onConfirm={() => {
          const updated = parkedSales.filter(s => s.id !== deletingId);
          writeParkedSales(updated);
          setParkedSales(updated);
          setDeletingId(null);
        }}
        onCancel={() => setDeletingId(null)}
      />
    </div>
  );
}

